import {
	CalendarCheck,
	ClipboardList,
	MessageCircleHeart,
	NotebookPen,
	PhoneCall,
	Salad,
	ShieldCheck,
	Sparkles,
	Stethoscope,
	UserCheck
} from "lucide-react"
import { imageBaseURL } from "@/lib/utils"
import type { ServiceId } from "./services"
import type { ProductAdvantage, ProductDescriptionBlock } from "./products-content"

export interface ServiceContent {
	slogan?: string
	description: ProductDescriptionBlock[]
	advantages: ProductAdvantage[]
	conditions?: string[]
	cta: { title: string; text?: string }
}

const CONTENT: Record<ServiceId, ServiceContent> = {
	dieta: {
		slogan: "Nutrición a la medida de tu tratamiento",
		description: [
			{
				image: imageBaseURL("services/dieta-1.webp"),
				heading: "Un plan hecho para ti",
				body: "Diseñamos tu dieta personalizada a partir de tu padecimiento, tus hábitos y el tratamiento Biosany que estás siguiendo. No es una dieta genérica: cada recomendación está pensada para potenciar los resultados de tu bioestimulación celular."
			},
			{
				image: imageBaseURL("services/consulta-1.webp"),
				heading: "Acompañamiento durante 4 semanas",
				body: "Recibes tu plan nutricional completo por correo o WhatsApp, con alimentos accesibles y fáciles de conseguir. Nuestro equipo da seguimiento a tu evolución y ajusta las recomendaciones cuando es necesario."
			}
		],
		advantages: [
			{
				icon: NotebookPen,
				title: "Plan 100% personalizado",
				text: "Ajustado a tu padecimiento y a tu tratamiento activo."
			},
			{
				icon: CalendarCheck,
				title: "Duración de 4 semanas",
				text: "Tiempo suficiente para generar un cambio de hábitos real."
			},
			{
				icon: Salad,
				title: "Alimentos naturales",
				text: "Recomendaciones con ingredientes accesibles, sin productos milagro."
			},
			{
				icon: MessageCircleHeart,
				title: "Entrega por email o WhatsApp",
				text: "Recibe tu plan y resuelve dudas desde donde estés."
			},
			{
				icon: ShieldCheck,
				title: "Compatible con tu tratamiento",
				text: "Pensada para complementar la bioestimulación celular, no para sustituirla."
			},
			{ icon: Sparkles, title: "Seguimiento a tu evolución", text: "Ajustamos el plan conforme avanzas." }
		],
		conditions: [
			"Sobrepeso y obesidad",
			"Diabetes tipo 2",
			"Menopausia",
			"Estreñimiento crónico",
			"Envejecimiento prematuro",
			"Retención de líquidos"
		],
		cta: {
			title: "Empieza tu dieta personalizada",
			text: "Recibe tu plan nutricional de 4 semanas por correo o WhatsApp"
		}
	},
	consulta: {
		slogan: "Consulta a un especialista en bioestimulación celular",
		description: [
			{
				image: imageBaseURL("services/consulta-2.webp"),
				heading: "Una llamada, mucha claridad",
				body: "Es una llamada telefónica de 20 minutos con un especialista en bioestimulación celular. En ella resolvemos tus dudas sobre tu padecimiento y te ayudamos a elegir o ajustar el tratamiento Biosany más adecuado para ti."
			},
			{
				image: imageBaseURL("services/consulta-1.webp"),
				heading: "Antes, durante o después de tu tratamiento",
				body: "Úsala para orientarte antes de comprar, para resolver dudas mientras sigues un tratamiento, o para dar seguimiento a tu evolución. Agendamos en el horario que mejor te convenga."
			}
		],
		advantages: [
			{ icon: PhoneCall, title: "De forma remota", text: "Sin necesidad de instalar nada ni desplazarte." },
			{
				icon: Stethoscope,
				title: "Especialista en bioestimulación celular",
				text: "Atención de una persona experta en nuestros tratamientos."
			},
			{
				icon: ClipboardList,
				title: "Orientación personalizada",
				text: "Recomendaciones según tu padecimiento y tu historial."
			},
			{ icon: UserCheck, title: "Resuelve dudas concretas", text: "Sobre dosis, duración o combinación de productos." },
			{ icon: CalendarCheck, title: "Agenda flexible", text: "Elige el horario que mejor te acomode." },
			{
				icon: MessageCircleHeart,
				title: "Trato cercano",
				text: "Una conversación directa, sin filas ni salas de espera."
			}
		],
		cta: {
			title: "Agenda tu consulta remota",
			text: "20 minutos con un especialista en bioestimulación celular"
		}
	}
}

export const getServiceContent = (id: ServiceId): ServiceContent => CONTENT[id]

export default CONTENT
